import React from 'react';
import { MenuItem, TextField } from '@mui/material';

const id = (v) => (v === null || v === undefined || v === '' ? '' : String(v));

/**
 * Department + Division pickers for a track. Tasks created on the track inherit
 * whatever is chosen here.
 *
 * @param {Object} props
 * @param {Array} props.departments Workspace departments.
 * @param {Array} props.divisions Workspace divisions (each carries `department`).
 * @param {{department: ?number, division: ?number}} props.value
 * @param {(updates: Object) => void} props.onChange Called with the fields that
 *   changed: picking a department drops a division outside it.
 * @param {Object} [props.fieldProps] Extra props for both TextFields.
 * @param {(field: React.ReactNode) => React.ReactNode} [props.wrapField] Wraps
 *   each field, e.g. in a Grid cell.
 */
const TrackDepartmentSelects = ({
  departments,
  divisions,
  value,
  onChange,
  fieldProps = {},
  wrapField = (field) => field,
}) => {
  const filteredDivisions = divisions.filter(
    (d) => id(d.department) === id(value.department)
  );

  const handleDepartmentChange = (raw) => {
    const department = raw === '' ? null : Number(raw);
    const updates = { department };
    const stillValid = divisions.some(
      (d) => id(d.id) === id(value.division) && id(d.department) === id(department)
    );
    if (!stillValid && value.division != null) updates.division = null;
    onChange(updates);
  };

  return (
    <>
      {wrapField(
        <TextField
          select
          label="Department"
          value={id(value.department)}
          onChange={(e) => handleDepartmentChange(e.target.value)}
          {...fieldProps}
        >
          <MenuItem value=""><em>None</em></MenuItem>
          {departments.map((department) => (
            <MenuItem key={department.id} value={id(department.id)}>
              {department.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {wrapField(
        <TextField
          select
          label="Division"
          value={id(value.division)}
          disabled={!value.department}
          onChange={(e) =>
            onChange({ division: e.target.value === '' ? null : Number(e.target.value) })
          }
          {...fieldProps}
        >
          <MenuItem value=""><em>None</em></MenuItem>
          {filteredDivisions.map((division) => (
            <MenuItem key={division.id} value={id(division.id)}>
              {division.name}
            </MenuItem>
          ))}
        </TextField>
      )}
    </>
  );
};

export default TrackDepartmentSelects;
